import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { X, MessageSquare, Search, Filter, Calendar as CalendarIcon, RefreshCw, Heart, User, Users } from "lucide-react";
import { api, fileUrl, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBookingNotifications } from "@/lib/useBookingNotifications";
import { useLiveEvents } from "@/lib/useLiveEvents";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import BookingAnswers from "@/components/BookingAnswers";
import HealthWarning from "@/components/HealthWarning";

const POLL_MS = 20000;

export default function BookingsListPanel() {
  const { admin } = useAuth();
  const isAdmin = ["developer", "admin", "manager", "supervisor"].includes(admin?.role);
  // Photos are visible to every dashboard/crew-management role — the only
  // surfaces that hide them are the public guest-device endpoints (iPads,
  // journey page, etc.), which never receive image_path in the first place.
  const canSeePhotos = !!admin?.role;
  const notify = useBookingNotifications({ appName: "Yacht Spa" });
  const [bookings, setBookings] = useState([]);
  const [crew, setCrew] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState({ crew_id: "all", status: "all", q: "", when: "upcoming" });
  const latestCreatedAt = useRef(null);
  const isFirstLoad = useRef(true);

  const buildParams = (extra = {}) => {
    const p = {};
    if (filters.crew_id && filters.crew_id !== "all") p.crew_id = filters.crew_id;
    if (filters.status && filters.status !== "all") p.status = filters.status;
    if (filters.q.trim()) p.q = filters.q.trim();
    if (filters.when === "upcoming") { p.start = new Date().toISOString(); p.end = new Date(Date.now() + 400 * 24 * 3600 * 1000).toISOString(); }
    if (filters.when === "past") { p.start = new Date(Date.now() - 400 * 24 * 3600 * 1000).toISOString(); p.end = new Date().toISOString(); p.order = "desc"; }
    return { ...p, ...extra };
  };

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/admin/bookings", { params: buildParams() });
      setBookings(data || []);
      if (data && data.length) {
        latestCreatedAt.current = data.reduce((mx, b) => (b.created_at > mx ? b.created_at : mx), latestCreatedAt.current || "");
      }
    } catch (e) {
      if (!silent) toast.error(formatErr(e.response?.data?.detail) || "Could not load bookings");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const pollNew = async () => {
    if (!latestCreatedAt.current) return;
    try {
      const { data } = await api.get("/admin/bookings", { params: { since: latestCreatedAt.current, order: "asc", sort: "created_at" } });
      if (data && data.length) {
        data.forEach((b) => {
          toast.success(`New booking · ${b.guest_name}`, {
            description: `${b.treatment_name} · ${new Date(b.start_at).toLocaleString()}`,
            duration: 8000,
          });
          notify(b);
        });
        latestCreatedAt.current = data.reduce((mx, b) => (b.created_at > mx ? b.created_at : mx), latestCreatedAt.current);
        // refresh list to include new bookings if they match current filter
        setRefreshing(true);
        await load(true);
        setRefreshing(false);
      }
    } catch { /* silent */ }
  };

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/admin/crew"); setCrew(data || []); } catch {}
    })();
  }, []);

  useEffect(() => { load(isFirstLoad.current ? false : true); isFirstLoad.current = false; /* eslint-disable-next-line */ }, [filters]);

  useEffect(() => {
    const t = setInterval(pollNew, POLL_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, []);

  // Real-time WebSocket wiring — instant refresh on any booking.* event.
  useLiveEvents(useCallback((ev) => {
    if (!ev || !ev.type || !ev.type.startsWith("booking.")) return;
    const b = ev.booking || {};
    if (ev.type === "booking.created") { notify(b); toast.success(`New booking · ${b.guest_name}`, { description: `${b.treatment_name} · ${new Date(b.start_at).toLocaleString()}`, duration: 8000 }); }
    else if (ev.type === "booking.cancelled") toast.warning(`Cancelled · ${b.guest_name}`);
    else if (ev.type === "booking.deleted") toast(`Removed · ${b.guest_name}`);
    load(true);
  }, [notify]));

  const crewName = (id) => crew.find((c) => c.id === id)?.name || "Any";

  // Group couples bookings (two docs sharing couple_booking_id) into one row.
  // Preserves the backend's ordering (upcoming asc / past desc): we emit each
  // composite at the position of the FIRST occurrence, and skip the sibling.
  const composites = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const b of bookings) {
      if (b.couple_booking_id) {
        if (seen.has(b.couple_booking_id)) continue;
        seen.add(b.couple_booking_id);
        const pair = bookings.filter((x) => x.couple_booking_id === b.couple_booking_id);
        pair.sort((a, c) => (a.created_at || "").localeCompare(c.created_at || ""));
        const primary = pair[0];
        const partner = pair[1] || null;
        out.push({
          id: primary.id,
          couple_booking_id: b.couple_booking_id,
          isCouple: !!partner,
          isFriendDual: !!partner && (primary.partner_kind === "friend" || partner.partner_kind === "friend"),
          isPending: !!partner && (primary.invite_status === "pending" || partner.invite_status === "pending"),
          primary, partner,
          start_at: primary.start_at,
          end_at: primary.end_at,
          duration_min: primary.duration_min,
          treatment_name: primary.treatment_name,
          crew_id: primary.crew_id,
          status: primary.status,
          price: (Number(primary.price) || 0) + (Number(partner?.price) || 0),
          guest_name: partner ? `${primary.guest_name} & ${partner.guest_name}` : primary.guest_name,
          notes: [primary.notes, partner?.notes].filter(Boolean).join(" · "),
        });
      } else {
        out.push({ id: b.id, ...b, isCouple: false, primary: b, partner: null });
      }
    }
    return out;
  }, [bookings]);

  const doCancel = async (item) => {
    const label = item.isCouple ? `couple booking for ${item.primary.guest_name} & ${item.partner.guest_name}` : `booking for ${item.guest_name}`;
    if (!window.confirm(`Cancel ${label}?`)) return;
    try {
      const ids = item.isCouple ? [item.primary.id, item.partner.id] : [item.primary?.id || item.id];
      for (const id of ids) {
        await api.put(`/admin/bookings/${id}`, { status: "cancelled" });
      }
      toast.success(item.isCouple ? "Couple booking cancelled" : "Booking cancelled");
      load(true);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Could not cancel booking"); }
  };

  return (
    <div>
      <div className="flex justify-between items-start mb-6 flex-wrap gap-4">
        <div>
          <h2 className="font-display text-3xl">Bookings list</h2>
          <p className="text-sm text-muted-foreground mt-1">Ordered by appointment time · updates automatically {refreshing && <RefreshCw className="inline h-3 w-3 animate-spin ml-2" />}</p>
        </div>
        <Button variant="outline" onClick={() => load()} className="rounded-full" data-testid="list-refresh">
          <RefreshCw className="h-3 w-3 mr-2" /> Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-card border border-border p-4 mb-6 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="bookings-filters">
        <div>
          <Label className="overline text-primary/60 text-[10px]">Search guest</Label>
          <div className="relative mt-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Guest name…" className="pl-9" data-testid="filter-q" />
          </div>
        </div>
        <div>
          <Label className="overline text-primary/60 text-[10px]">Therapist</Label>
          <Select value={filters.crew_id} onValueChange={(v) => setFilters({ ...filters, crew_id: v })}>
            <SelectTrigger className="mt-1" data-testid="filter-crew"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All therapists</SelectItem>
              {crew.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="overline text-primary/60 text-[10px]">Status</Label>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger className="mt-1" data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="overline text-primary/60 text-[10px]">When</Label>
          <Select value={filters.when} onValueChange={(v) => setFilters({ ...filters, when: v })}>
            <SelectTrigger className="mt-1" data-testid="filter-when"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All time</SelectItem>
              <SelectItem value="upcoming">Upcoming (first to be done)</SelectItem>
              <SelectItem value="past">Past (newest first)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="text-muted-foreground text-sm">Loading…</div>
      ) : composites.length === 0 ? (
        <div className="border border-dashed border-border p-10 text-center text-muted-foreground" data-testid="list-empty">
          No bookings match your filters.
        </div>
      ) : (
        <div className="space-y-3" data-testid="bookings-list">
          {composites.map((b) => {
            const dt = new Date(b.start_at);
            const past = dt < new Date();
            const cardClass = b.status === "cancelled"
              ? "bg-card border border-border opacity-60"
              : b.isPending
                ? "bg-emerald-500/5 border border-dashed border-emerald-600/60"
                : b.isFriendDual
                  ? "bg-emerald-500/10 border border-emerald-600/50"
                  : b.isCouple
                    ? "bg-accent/10 border border-accent/50"
                    : "bg-card border border-border";
            return (
              <div
                key={b.id}
                onClick={() => setSelected(b)}
                className={`${cardClass} p-5 hover-lift cursor-pointer`}
                data-testid={`list-booking-${b.id}`}
              >
                <div className="grid grid-cols-1 md:grid-cols-[140px_1fr_auto] gap-4 items-start">
                  <div>
                    <div className="font-display text-lg leading-tight">{dt.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}</div>
                    <div className="text-xs text-muted-foreground">{dt.toTimeString().slice(0,5)} · {b.duration_min} min</div>
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {b.isFriendDual && <Users className="h-3.5 w-3.5 text-emerald-600" strokeWidth={2.5} data-testid={`list-friend-icon-${b.id}`} />}
                      {b.isCouple && !b.isFriendDual && <Heart className="h-3.5 w-3.5 text-accent" strokeWidth={2.5} data-testid={`list-couple-icon-${b.id}`} />}
                      {canSeePhotos && (
                        <span className="flex -space-x-2 mr-1" data-testid={`list-avatars-${b.id}`}>
                          {[b.primary, b.partner].filter(Boolean).map((g) => (
                            g.guest_image_path ? (
                              <img
                                key={g.id}
                                src={fileUrl(g.guest_image_path)}
                                alt={g.guest_name}
                                title={g.guest_name}
                                className="w-8 h-8 rounded-full object-cover border-2 border-card"
                                data-testid={`list-avatar-${g.id}`}
                              />
                            ) : (
                              <span
                                key={g.id}
                                className="w-8 h-8 rounded-full bg-muted border-2 border-card flex items-center justify-center"
                                title={g.guest_name}
                              >
                                <User className="h-3.5 w-3.5 text-muted-foreground" />
                              </span>
                            )
                          ))}
                        </span>
                      )}
                      <span className="font-medium">{b.guest_name}</span>
                      <HealthWarning
                        size="sm"
                        testId={`list-health-warn-${b.id}`}
                        guests={[
                          { name: b.primary?.guest_name, snapshot: b.primary?.health_snapshot },
                          { name: b.partner?.guest_name, snapshot: b.partner?.health_snapshot },
                        ]}
                      />
                      <span className="text-muted-foreground text-sm">·</span>
                      <span className="text-sm">{b.treatment_name}</span>
                      {b.isPending && <Badge className="text-[10px] bg-emerald-600/15 text-emerald-700 dark:text-emerald-400 border border-dashed border-emerald-600/50 hover:bg-emerald-600/15">Confirmation pending</Badge>}
                      {b.isFriendDual && !b.isPending && <Badge className="text-[10px] bg-emerald-600/20 text-emerald-700 dark:text-emerald-400 border-0 hover:bg-emerald-600/20">Friend dual</Badge>}
                      {b.isCouple && !b.isFriendDual && <Badge className="text-[10px] bg-accent/25 text-accent border-0 hover:bg-accent/25">Couples</Badge>}
                      {b.crew_id && <Badge variant="outline" className="text-[10px]">{crewName(b.crew_id)}</Badge>}
                      {b.status === "cancelled" && <Badge variant="outline" className="text-[10px] border-destructive/40 text-destructive">Cancelled</Badge>}
                      {!past && b.status !== "cancelled" && <Badge className="text-[10px] bg-accent/15 text-accent border-0 hover:bg-accent/15">Upcoming</Badge>}
                    </div>
                    {b.notes && (
                      <div className="mt-3 flex gap-2 items-start bg-muted/60 border-l-2 border-accent p-3" data-testid={`list-notes-${b.id}`}>
                        <MessageSquare className="h-3.5 w-3.5 text-accent mt-0.5 flex-shrink-0" />
                        <div className="text-sm text-foreground/85 whitespace-pre-wrap break-words">{b.notes}</div>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2 min-w-[90px]">
                    <span className="font-display text-lg text-accent">€{Number(b.price || 0).toFixed(0)}</span>
                    {isAdmin && b.status !== "cancelled" && !past && (
                      <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); doCancel(b); }} className="text-destructive border-destructive/40" data-testid={`list-cancel-${b.id}`}>
                        <X className="h-3 w-3 mr-1" /> Cancel{b.isCouple ? " both" : ""}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent data-testid="list-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              {selected?.isCouple && <Heart className="h-5 w-5 text-accent" strokeWidth={2} />}
              {selected?.isCouple ? "Couples booking" : "Booking details"}
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div className="overline text-primary/50">Treatment</div><div>{selected.treatment_name}</div>
                <div className="overline text-primary/50">When</div><div>{new Date(selected.start_at).toLocaleString()}</div>
                <div className="overline text-primary/50">Duration</div><div>{selected.duration_min} min</div>
                <div className="overline text-primary/50">Therapist</div><div>{crewName(selected.crew_id)}</div>
                <div className="overline text-primary/50">Status</div><div className="capitalize">{selected.status}</div>
              </div>

              {[selected.primary, selected.partner].filter(Boolean).map((g, idx) => (
                <div key={g.id} className={`border ${selected.isCouple ? "border-accent/40 bg-accent/5" : "border-border bg-muted/40"} p-4 space-y-2`} data-testid={`list-detail-guest-${idx}`}>
                  <div className="flex items-center gap-3">
                    {canSeePhotos && (
                      g.guest_image_path ? (
                        <img
                          src={fileUrl(g.guest_image_path)}
                          alt={g.guest_name}
                          className="w-14 h-14 rounded-full object-cover border border-border"
                          data-testid={`list-detail-avatar-${idx}`}
                        />
                      ) : (
                        <div className="w-14 h-14 rounded-full bg-muted border border-border flex items-center justify-center">
                          <User className="h-5 w-5 text-muted-foreground" />
                        </div>
                      )
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="overline text-primary/60">
                        {selected.isCouple ? (idx === 0 ? "Guest A" : "Guest B") : "Guest"}
                      </div>
                      <div className="font-display text-lg truncate flex items-center gap-1.5">
                        {g.guest_name}
                        <HealthWarning
                          size="md"
                          testId={`list-detail-health-warn-${idx}`}
                          guests={[{ snapshot: g.health_snapshot }]}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                    {g.guest_email && (<>
                      <div className="text-muted-foreground">Email</div><div className="truncate">{g.guest_email}</div>
                    </>)}
                  </div>
                  {g.notes && (
                    <div>
                      <div className="overline text-accent mt-2 mb-1 flex items-center gap-1"><MessageSquare className="h-3 w-3" /> Special request</div>
                      <div className="bg-background border-l-2 border-accent p-2 text-xs whitespace-pre-wrap">{g.notes}</div>
                    </div>
                  )}
                  <BookingAnswers answers={g.answers} compact />
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
